import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import './assets/fonts/fonts.css'
import './assets/tailwind.css'

// 避免 ResizeObserver 同帧循环告警（Element Plus 等库会触发）
const _RO = window.ResizeObserver
window.ResizeObserver = class ResizeObserver extends _RO {
  constructor(callback) {
    super((entries, observer) => {
      requestAnimationFrame(() => { callback(entries, observer) })
    })
  }
}
// 抑制未捕获的 ResizeObserver 报错，避免 dev overlay 弹出
window.addEventListener('error', (e) => {
  if (e.message && (e.message.includes('ResizeObserver') && e.message.includes('undelivered'))) {
    e.stopImmediatePropagation()
    return true
  }
})

// i18n
import i18n from './i18n'

// Element Plus
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import zhTw from 'element-plus/es/locale/lang/zh-tw'
import en from 'element-plus/es/locale/lang/en'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import { ElMessageBox, ElImageViewer, ElImage } from 'element-plus'

// 图片预览：点击遮罩区域关闭
if (ElImageViewer.props?.hideOnClickModal) {
  ElImageViewer.props.hideOnClickModal = {
    type: Boolean,
    default: true
  }
}
if (ElImage.props?.hideOnClickModal) {
  ElImage.props.hideOnClickModal = {
    type: Boolean,
    default: true
  }
}

// 为 ElMessageBox 的 alert/confirm/prompt 统一注入默认 lockScroll: false
function mergeLockScrollFalse(args) {
  // 最后一个参数可能是 options 或 appContext（Vue 注入），只对像 options 的对象合并
  const last = args[args.length - 1]
  const isOptions = last && typeof last === 'object' && !Array.isArray(last) && !last.then && !('app' in last && 'config' in last)
  if (isOptions) {
    args[args.length - 1] = { ...last, lockScroll: last.lockScroll ?? false }
  } else if (args.length === 1 || (args.length >= 2 && typeof args[args.length - 1] !== 'object')) {
    args.push({ lockScroll: false })
  }
  return args
}
const rawAlert = ElMessageBox.alert.bind(ElMessageBox)
const rawConfirm = ElMessageBox.confirm.bind(ElMessageBox)
const rawPrompt = ElMessageBox.prompt.bind(ElMessageBox)
ElMessageBox.alert = function (...args) {
  return rawAlert(...mergeLockScrollFalse(args))
}
ElMessageBox.confirm = function (...args) {
  return rawConfirm(...mergeLockScrollFalse(args))
}
ElMessageBox.prompt = function (...args) {
  return rawPrompt(...mergeLockScrollFalse(args))
}

// Vue3 无缝滚动
import Vue3SeamlessScroll from 'vue3-seamless-scroll'

// import {common} from "@/tool/common.js";

import { localTruncateTextFunction } from './utils/textUtils'
import { auth } from './utils/auth'

// 根据当前 i18n 语言获取 Element Plus 语言包
const getElementPlusLocale = () => {
  const currentLocale = i18n.global.locale.value
  if (currentLocale === 'zh-tw') {
    return zhTw
  } else if (currentLocale === 'zh-cn') {
    return zhCn
  } else {
    return en
  }
}

const app = createApp(App)

// 配置全局错误处理
app.config.errorHandler = (err, instance, info) => {
  console.error('Caught by global errorHandler:', err, instance, info)
}

// 全局方法挂载（Vue 3 方式）
app.config.globalProperties.$localTruncateTextFunction = localTruncateTextFunction
// 全局鉴权对象挂载
app.config.globalProperties.$auth = auth

// 全局注册 Element-Plus 图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

// 使用插件
app.use(router)
app.use(i18n) // 使用 i18n
app.use(ElementPlus, {
  locale: getElementPlusLocale(), // 根据 i18n 语言动态设置 Element Plus 语言
})
app.use(Vue3SeamlessScroll)

// 挂载应用
app.mount('#app')
